import { useEffect, useState } from 'react';
import axios from 'axios';
import Banner from './Banner/Banner';
import CategorySidebar from './CategorySidebar';
import SidePromo from './SidePromo';
import TrustSection from './TrustSection';
import CategoryGridFooter from './CategoryGridFooter';
import ProductSlider from './ProductSlider/ProductSlider';
import MetaData from '../Layouts/MetaData';
import { useLang } from '../../utils/i18n';

const Home = () => {
  const { lang } = useLang();
  const [promoCards, setPromoCards] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPromoCards = async () => {
      try {
        const { data } = await axios.get('/api/v1/promo-cards');
        setPromoCards(data.cards);
      } catch (err) {
        console.error('Failed to load promo cards', err);
      }
    };
    fetchPromoCards();

    const fetchSections = async () => {
      try {
        const { data } = await axios.get('/api/v1/product-sections');
        setSections(data.sections);
      } catch (err) {
        console.error('Failed to load product sections', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSections();
  }, []);

  // Returns the admin-created promo card for this slot, or null if there
  // aren't enough cards to fill it — in which case that row just shows
  // the product slider alone, full-width, with no filler content.
  const websitePromoCards = promoCards.filter((c) => c.showOnWebsite !== false);

  const getPromo = (index) => {
    if (index >= websitePromoCards.length) return null;

    const card = websitePromoCards[index];
    const useZh = lang === 'zh';
    return {
      eyebrow: (useZh && card.eyebrowZh) ? card.eyebrowZh : card.eyebrow,
      title: (useZh && card.titleZh) ? card.titleZh : card.title,
      subtitle: (useZh && card.subtitleZh) ? card.subtitleZh : card.subtitle,
      price: card.price,
      bg: card.bg,
      image: ((useZh && card.imageZh?.url) ? card.imageZh : card.image)?.url,
      linkType: card.linkType,
      linkProduct: card.linkProduct,
      linkCategory: card.linkCategory,
      linkBrand: card.linkBrand,
      linkCustomUrl: card.linkCustomUrl,
      buttonText: (useZh && card.buttonTextZh) ? card.buttonTextZh : card.buttonText,
      showOverlay: card.showOverlay,
      showButton: card.showButton,
      buttonPosition: card.buttonPosition,
    };
  };

  return (
    <>
      <MetaData title="Fresh Groceries Online | Best Prices, Fast Delivery" />
      <main className="flex flex-col gap-8 w-full sm:w-[95%] m-auto px-2 sm:px-0">
        <div className="flex gap-8 items-stretch mt-6">
          <CategorySidebar />
          <div className="flex-1 min-w-0 self-start">
            <Banner />
          </div>
        </div>

        {!loading && sections.filter((s) => s.showOnWebsite !== false).map((section, i) => {
          const promo = getPromo(i);
          return (
            <div className="flex gap-8 items-stretch" key={section._id}>
              {promo && <SidePromo {...promo} />}
              <div className="flex-1 min-w-0">
                <ProductSlider
                  title={lang === 'zh' && section.headingZh ? section.headingZh : section.heading}
                  tagline={lang === 'zh' && section.subheadingZh ? section.subheadingZh : section.subheading}
                  products={section.products}
                  sectionId={section._id}
                />
              </div>
            </div>
          );
        })}

        {!loading && sections.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-400">
            No product sections configured yet — add some from the admin panel.
          </div>
        )}

        <TrustSection />
        <CategoryGridFooter />
      </main>
    </>
  );
};

export default Home;
